import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLogo } from '../../context/LogoContext';
import {
  LayoutDashboard, Target, FileText, Newspaper, Image, Globe, Users,
  Shield, Settings, Tv, LogOut, Sun, Moon, Radio,
  Menu, ChevronLeft, ArrowLeftRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../ui/sheet';

const LogoImage = ({ size = 'w-8 h-8' }) => {
  const { logoUrl } = useLogo();
  if (logoUrl) {
    return (
      <div className={`${size} rounded overflow-hidden flex-shrink-0 bg-muted border border-border`}>
        <img src={logoUrl} alt="" className="w-full h-full object-contain p-0.5" />
      </div>
    );
  }
  return (
    <div className={`${size} rounded bg-red-600 flex items-center justify-center shadow-lg shadow-red-900/30 flex-shrink-0`}>
      <Radio className="w-4 h-4 text-white" />
    </div>
  );
};

// V14.10.3 — sidebar items use EMS-aligned roles. Each item declares which
// roles can SEE it. Observer + Simcell are tightly scoped per the V14.10.3
// requirements: only Dashboard / Exercises / Settings / Live Display
// (everything else — Social Posts, News Articles, Media Library, Platforms —
// is super_admin-only). Participants get NO nav items (kiosk-light).
const allNavItems = [
  { path: '/mp/dashboard',     label: 'Dashboard',     icon: LayoutDashboard, roles: ['super_admin', 'simcel'] },
  { path: '/mp/exercises',     label: 'Exercises',     icon: Target,          roles: ['super_admin', 'simcel', 'observer'] },
  { path: '/mp/social-posts',  label: 'Social Posts',  icon: FileText,        roles: ['super_admin'] },
  { path: '/mp/news-posts',    label: 'News Articles', icon: Newspaper,       roles: ['super_admin'] },
  { path: '/mp/media',         label: 'Media Library', icon: Image,           roles: ['super_admin'] },
  { path: '/mp/platforms',     label: 'Platforms',     icon: Globe,           roles: ['super_admin'] },
  { path: '/mp/users',         label: 'Users',         icon: Users,           roles: ['super_admin'] },
  { path: '/mp/audit',         label: 'Audit Logs',    icon: Shield,          roles: ['super_admin'] },
  { path: '/mp/settings',      label: 'Settings',      icon: Settings,        roles: ['super_admin', 'simcel', 'observer'] },
];

// V14.7 — translate any role/alias to the canonical MP role set.
// EMS roles ALWAYS take precedence over `mp_roles` because they reflect the
// user's actual position in the unified Hub. A participant in EMS is a
// participant in MP regardless of any stale `mp_roles` carryover from the
// pre-unification era.
function getUserMpRoles(user) {
  const emsRoles = user?.roles || [];
  // Hub-level admins → super_admin everywhere
  if (emsRoles.includes('super_admin') || emsRoles.includes('hub_admin')) {
    return ['super_admin'];
  }
  // Otherwise translate the highest EMS role we find (write > read > kiosk)
  const out = new Set();
  for (const r of emsRoles) {
    if (r === 'simcel' || r === 'controller') out.add('simcel');
    else if (r === 'observer') out.add('observer');
    else if (r === 'participant') out.add('participant');
  }
  // If any EMS role was recognised, that's the truth — don't pull from mp_roles
  if (out.size > 0) return [...out];

  // Fallback: legacy mp_roles (for accounts that exist only in mp_users)
  for (const r of (user?.mp_roles || [])) {
    if (r === 'admin') out.add('super_admin');
    else if (r === 'author' || r === 'simcel' || r === 'controller') out.add('simcel');
    else if (r === 'viewer' || r === 'observer') out.add('observer');
    else if (r === 'user' || r === 'participant') out.add('participant');
  }
  return [...out];
}

function filterNavByRole(mpRoles) {
  // Participants get NO main-nav items (kiosk mode — only Live Display below).
  if (mpRoles.includes('participant') &&
      !mpRoles.includes('super_admin') &&
      !mpRoles.includes('simcel') &&
      !mpRoles.includes('observer')) {
    return [];
  }
  return allNavItems.filter(item => item.roles.some(r => mpRoles.includes(r)));
}

function isParticipantOnly(mpRoles) {
  return mpRoles.includes('participant') &&
    !mpRoles.includes('super_admin') &&
    !mpRoles.includes('simcel') &&
    !mpRoles.includes('observer');
}

const SidebarContent = ({ location, user, theme, toggleTheme, handleLogout, navItems, participantOnly, hasEmsAccess, onSwitchToEms }) => {
  const isDark = theme === 'dark';

  return (
    <div className={`flex flex-col h-full border-r transition-colors ${
      isDark ? 'bg-card text-foreground border-border' : 'bg-white text-foreground border-border'
    }`}>
      {/* Logo header */}
      <div className={`p-4 border-b ${isDark ? 'border-border' : 'border-border'}`}>
        <div className="flex items-center gap-2.5">
          <LogoImage size="w-8 h-8" />
          <div>
            <span className={`font-heading font-bold text-sm tracking-wider uppercase ${isDark ? 'text-foreground' : 'text-foreground'}`}>
              Media Portal
            </span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
              </span>
              <span className="text-[9px] font-medium text-red-400 uppercase tracking-widest">
                {participantOnly ? 'Participant View' : 'Newsroom Command'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Hub — V14.10.3: always visible (also for participants) */}
      <div className="px-3 pt-3 space-y-2">
        <Link
          to="/hub"
          className={`flex items-center gap-2 px-3 py-2 rounded text-xs border border-dashed transition-all ${
            isDark
              ? 'text-muted-foreground hover:text-foreground hover:bg-muted/60 border-border'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted border-border'
          }`}
          data-testid="mp-back-to-hub"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Hub
        </Link>
        {hasEmsAccess && (
          <button
            onClick={onSwitchToEms}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs border border-dashed border-red-500/30 bg-red-500/5 text-red-500 hover:bg-red-500/10 transition-all"
            data-testid="mp-switch-to-ems"
          >
            <Target className="w-3.5 h-3.5" /> Switch to EMS <ArrowLeftRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map(item => {
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-all ${
                active
                  ? 'bg-red-600/15 text-red-500 font-medium border-l-2 border-red-500 ml-0 pl-[10px]'
                  : isDark
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`mp-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className={`w-4 h-4 ${active ? 'text-red-500' : ''}`} />
              {item.label}
            </Link>
          );
        })}

        {/* Live Feed link — always visible */}
        <Link
          to="/mp/public-display"
          className={`flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-all ${
            location.pathname === '/mp/public-display'
              ? 'bg-red-600/15 text-red-500 font-medium border-l-2 border-red-500 ml-0 pl-[10px]'
              : isDark
                ? 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
          data-testid="mp-nav-live-display"
        >
          <Tv className="w-4 h-4" /> Live Display
        </Link>
      </nav>

      {/* User section */}
      <div className={`p-3 border-t ${isDark ? 'border-border' : 'border-border'}`}>
        <div className="flex items-center gap-2 px-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-red-600/20 flex items-center justify-center text-xs font-bold text-red-400">
            {user?.name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-medium truncate ${isDark ? 'text-foreground' : 'text-foreground'}`}>{user?.name}</p>
            <p className={`text-[10px] truncate ${isDark ? 'text-muted-foreground' : 'text-muted-foreground'}`}>{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={toggleTheme}
            className={`h-7 w-7 p-0 ${isDark ? 'text-muted-foreground hover:text-foreground hover:bg-muted' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
            data-testid="mp-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </Button>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-2 flex-1 px-3 py-1.5 rounded text-xs transition-all ${
              isDark ? 'text-muted-foreground hover:text-foreground hover:bg-muted/60' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            data-testid="mp-sign-out"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

const MPDashboardLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const mpRoles = getUserMpRoles(user);
  const navItems = filterNavByRole(mpRoles);
  const participantOnly = isParticipantOnly(mpRoles);
  // V14.10.3 — every signed-in Hub user has EMS access. The switch button
  // is shown for everyone except guest-only sessions.
  const hasEmsAccess = !!(user?.roles?.length);
  const onSwitchToEms = async () => {
    // V14.10.9 — if we're on an MP exercise page and it's bridged to an
    // EMS exercise, jump straight to that linked EMS exercise.
    const m = location.pathname.match(/^\/mp\/(?:exercise|exercises)\/([^/?#]+)/);
    if (m && m[1]) {
      try {
        const { mediaPortalApi } = await import('../../lib/api');
        const s = await mediaPortalApi.getSettingsByMp(m[1]);
        if (s?.linked && s?.ems_exercise_id) {
          navigate(`/exercises/${s.ems_exercise_id}`);
          return;
        }
      } catch { /* fall through to default */ }
    }
    // Route participants to their dashboard, controllers to /exercises.
    const emsRoles = user?.roles || [];
    if (emsRoles.includes('participant') &&
        !emsRoles.includes('super_admin') &&
        !emsRoles.includes('simcel') &&
        !emsRoles.includes('hub_admin') &&
        !emsRoles.includes('observer')) {
      navigate('/participant');
    } else {
      navigate('/exercises');
    }
  };

  // V14.10.3 — Participants are no longer hard-redirected to /mp/public-display.
  // They can now navigate to /hub and switch to EMS. Live Display remains their
  // default landing page via the existing /hub → /mp logic in LoginPage.

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isDark = theme === 'dark';
  const sidebarProps = { location, user, theme, toggleTheme, handleLogout, navItems, participantOnly, hasEmsAccess, onSwitchToEms };

  return (
    <div className={`min-h-screen flex transition-colors ${isDark ? 'bg-background text-foreground' : 'bg-muted text-foreground'}`}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-60 flex-shrink-0" data-testid="mp-sidebar">
        <div className="fixed inset-y-0 left-0 w-60">
          <SidebarContent {...sidebarProps} />
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent side="left" className={`w-60 p-0 ${isDark ? 'bg-card border-border' : 'bg-white border-border'}`}>
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SheetDescription className="sr-only">Access main navigation and settings.</SheetDescription>
          <SidebarContent {...sidebarProps} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <div className={`border-b sticky top-0 z-30 backdrop-blur-sm ${
          isDark ? 'border-border bg-card/80' : 'border-border bg-white/80'
        }`}>
          <div className="flex items-center gap-3 px-5 lg:px-8 py-3">
            <Button variant="ghost" size="icon"
              className={`lg:hidden h-8 w-8 ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMobileSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>

            <div className="flex items-center gap-2 flex-1">
              <div className="w-1 h-5 bg-red-600 rounded-full" />
              <h1 className={`text-base lg:text-lg font-heading font-bold tracking-tight uppercase ${isDark ? 'text-foreground' : 'text-foreground'}`}>
                {title}
              </h1>
            </div>

            <div className={`hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border ${
              isDark ? 'bg-muted/60 border-border/50' : 'bg-red-50 border-red-200'
            }`}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="text-[10px] text-red-500 font-semibold uppercase tracking-wider">Live</span>
            </div>
          </div>
        </div>

        <div className="p-5 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MPDashboardLayout;
